import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@mdvip/shared/components/ui/accordion';
import { Button } from '@mdvip/shared/components/ui/button';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@mdvip/shared/components/ui/sheet';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Facet, FacetValue } from './ContentFilters';
import { CFGroup, countSelected } from './ContentFilters';

export interface ContentFiltersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facets?: Facet[];
  value?: FacetValue;
  onChange?: (facetId: string, values: string[]) => void;
  onClearAll: () => void;
  /** Count printed on the primary action ("Show {resultCount} results"). */
  resultCount: number;
  /** Sheet title. Defaults to "Filters" — this drawer never carries a sort section, so never "Filter & sort". */
  title?: string;
  /**
   * Caller-owned content at the top of the scroll area, above the facet accordions — for a filter
   * that isn't a taxonomy facet (e.g. a saved-only toggle). Shape it like an accordion section so
   * it reads as one of them.
   */
  leading?: React.ReactNode;
  /**
   * Active filters `value` doesn't know about (i.e. whatever `leading` owns), so Clear All can tell
   * "nothing is active" from "only the caller's own control is".
   */
  extraActiveCount?: number;
  className?: string;
}

/**
 * ContentFiltersDrawer — the phone half of the same pattern as `ContentFilters`:
 * the same facets as stacked checkbox groups in a bottom sheet. Checking an
 * option applies it immediately via `onChange`, same as the rail — the sheet
 * simply covers the list while that happens, so nothing visibly shuffles until
 * "Show N results" closes it. Shares its facet-rendering internals (`CFGroup`)
 * with `ContentFilters`.
 *
 * No sort section — the real design's drawer withholds it entirely when the
 * caller has no sort options, which is every case this ticket needs.
 */
export function ContentFiltersDrawer({
  open,
  onOpenChange,
  facets = [],
  value = {},
  onChange,
  onClearAll,
  resultCount,
  title,
  leading,
  extraActiveCount = 0,
  className,
}: ContentFiltersDrawerProps) {
  const { t } = useTranslation('shared');
  const total = countSelected(value) + extraActiveCount;
  const selectedOpen = facets.filter((f) => (value[f.id] ?? []).length).map((f) => f.id);
  const drawerOpen = selectedOpen.length ? selectedOpen : facets.slice(0, 1).map((f) => f.id);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-slot="content-filters-drawer"
        className={cn('flex max-h-[88dvh] flex-col gap-0 rounded-t-2xl p-0', className)}
      >
        <SheetHeader className="border-b py-4">
          <SheetTitle>{title ?? t('contentFilters.heading')}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-2">
          {leading}
          <Accordion multiple defaultValue={drawerOpen}>
            {facets.map((f) => {
              const selected = value[f.id] ?? [];
              return (
                <AccordionItem key={f.id} value={f.id} className="border-b last:border-b-0">
                  <AccordionTrigger className="py-4 font-semibold">
                    <span>
                      {f.label}
                      {selected.length ? t('contentFilters.selectedCount', { count: selected.length }) : ''}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <CFGroup facet={f} selected={selected} onChange={onChange} limit={8} hideLegend />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
        <SheetFooter className="flex-row gap-3 border-t px-4 py-4">
          <Button variant="outline" className="min-h-11 flex-1" onClick={onClearAll} disabled={!total}>
            {t('contentFilters.clearAll')}
          </Button>
          <Button className="min-h-11 flex-[2]" onClick={() => onOpenChange(false)}>
            {t('contentFilters.showResults', { count: resultCount })}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
